import { apiGet, apiDelete, apiPost } from "../utils/api";

/**
 * Service pour gérer les récompenses Twitch via le backend
 */

/**
 * Récupère toutes les récompenses personnalisées
 * @param {Object} options - Options de la requête
 * @param {boolean} options.onlyManageable - Si true, retourne uniquement les récompenses gérables
 * @returns {Promise<Array>} Liste des récompenses
 */
export async function fetchCustomRewards({ onlyManageable = false } = {}) {
  try {
    const params = new URLSearchParams();
    if (onlyManageable) {
      params.append("only_manageable", "true");
    }

    const endpoint = `/twitch/rewards${
      params.toString() ? `?${params.toString()}` : ""
    }`;
    const response = await apiGet(endpoint);

    return response.data || [];
  } catch (error) {
    console.error("Erreur lors de la récupération des récompenses:", error);
    throw error;
  }
}

/**
 * Récupère une récompense spécifique par son ID
 * @param {string} rewardId - ID de la récompense
 * @returns {Promise<Object>} Détails de la récompense
 */
export async function fetchRewardById(rewardId) {
  try {
    const response = await apiGet(`/twitch/rewards/${rewardId}`);
    return response.data;
  } catch (error) {
    console.error("Erreur lors de la récupération de la récompense:", error);
    throw error;
  }
}

/**
 * Récupère les rédactions (redemptions) d'une récompense
 * @param {Object} options - Options de la requête
 * @param {string} options.rewardId - ID de la récompense
 * @param {string} options.status - Statut des rédactions (UNFULFILLED, FULFILLED, CANCELED)
 * @param {number} options.limit - Nombre maximum de résultats (max 50)
 * @returns {Promise<Array>} Liste des rédactions
 */
export async function fetchRewardRedemptions({
  rewardId,
  status = "FULFILLED",
  limit = 50,
}) {
  try {
    const params = new URLSearchParams();
    params.append("status", status);
    params.append("limit", limit.toString());

    const response = await apiGet(
      `/twitch/rewards/${rewardId}/redemptions?${params.toString()}`
    );

    return response.data || [];
  } catch (error) {
    console.error("Erreur lors de la récupération des rédactions:", error);
    throw error;
  }
}

/**
 * @deprecated Utilisez fetchCustomRewards() à la place
 * Ancienne fonction pour compatibilité - utilise directement l'API Twitch
 */
export async function fetchCustomRewardsLegacy({
  broadcasterId,
  clientId,
  token,
}) {
  const url = `https://api.twitch.tv/helix/channel_points/custom_rewards?broadcaster_id=${broadcasterId}`;

  const response = await fetch(url, {
    headers: {
      "Client-ID": clientId,
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.message || "Erreur lors de la récupération des récompenses."
    );
  }

  const data = await response.json();
  return data.data;
}

/**
 * @deprecated Utilisez fetchRewardRedemptions() à la place
 * Ancienne fonction pour compatibilité - utilise directement l'API Twitch
 */
export async function fetchRewardRedemptionsLegacy({
  broadcasterId,
  rewardId,
  clientId,
  token,
  status = "FULFILLED",
}) {
  let allRedemptions = [];
  let cursor = null;

  do {
    const url = new URL(
      "https://api.twitch.tv/helix/channel_points/custom_rewards/redemptions"
    );
    url.searchParams.append("broadcaster_id", broadcasterId);
    url.searchParams.append("reward_id", rewardId);
    url.searchParams.append("status", status);
    url.searchParams.append("first", "50");
    if (cursor) url.searchParams.append("after", cursor);

    const response = await fetch(url.toString(), {
      headers: {
        "Client-ID": clientId,
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.message || "Erreur lors de la récupération des redemptions."
      );
    }

    const data = await response.json();
    allRedemptions = allRedemptions.concat(data.data);
    cursor = data.pagination?.cursor || null;
  } while (cursor);

  return allRedemptions;
}

/**
 * Supprime une récompense personnalisée
 * @param {string} rewardId - ID de la récompense à supprimer
 * @returns {Promise<Object>} Réponse de l'API
 */
export async function deleteCustomReward(rewardId) {
  try {
    const response = await apiDelete(`/twitch/rewards/${rewardId}`);
    return response;
  } catch (error) {
    console.error("Erreur lors de la suppression de la récompense:", error);
    throw error;
  }
}

/**
 * Crée une récompense Channel Points via l’API Helix (token broadcaster).
 * @param {{ title: string, prompt: string, cost: number, is_enabled?: boolean }} payload
 * @returns {Promise<Object>} Objet récompense créée (réponse Twitch)
 */
export async function createCustomReward({
  title,
  prompt,
  cost,
  is_enabled = true,
}) {
  const response = await apiPost("/twitch/rewards", {
    title: String(title).trim(),
    prompt: String(prompt).trim(),
    cost: typeof cost === "number" ? cost : parseInt(String(cost), 10),
    is_enabled,
  });
  if (!response.success || !response.data) {
    throw new Error(response.error || "Création de la récompense impossible.");
  }
  return response.data;
}
